import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Clock, X, ArrowRight } from 'lucide-react';
import axios from 'axios';

import { API_BASE as API } from '../config';
const DISMISS_KEY = 'trial_banner_dismissed';

export default function TrialBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [daysLeft, setDaysLeft] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Check if dismissed this session
    const d = sessionStorage.getItem(DISMISS_KEY);
    if (d === 'true') {
      setDismissed(true);
      setLoaded(true);
      return;
    }
    axios.get(`${API}/billing/subscription`, { withCredentials: true })
      .then(res => {
        const info = res.data?.trial_info;
        if (info?.is_trial && info.days_remaining <= 7) {
          setDaysLeft(info.days_remaining);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [user]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, 'true');
  };

  if (!loaded || dismissed || daysLeft === null) return null;

  // Color scheme based on urgency
  let bg, text, border, btnBg, btnText;
  if (daysLeft <= 1) {
    bg = 'bg-red-50'; text = 'text-red-800'; border = 'border-red-200';
    btnBg = 'bg-red-600 hover:bg-red-700'; btnText = 'text-white';
  } else if (daysLeft <= 3) {
    bg = 'bg-amber-50'; text = 'text-amber-800'; border = 'border-amber-200';
    btnBg = 'bg-amber-600 hover:bg-amber-700'; btnText = 'text-white';
  } else {
    bg = 'bg-blue-50'; text = 'text-blue-800'; border = 'border-blue-200';
    btnBg = 'bg-blue-600 hover:bg-blue-700'; btnText = 'text-white';
  }

  const dayLabel = daysLeft === 0 ? 'Today' : daysLeft === 1 ? '1 day' : `${daysLeft} days`;

  return (
    <div className={`${bg} ${border} border-b px-3 py-2 md:px-4 md:py-2.5`} data-testid="trial-expiry-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
        <div className={`flex items-center gap-2 ${text} text-xs md:text-sm flex-1 min-w-0`}>
          <Clock className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">
            {daysLeft === 0
              ? 'Your free trial expires today!'
              : `Your free trial ends in ${dayLabel}. Upgrade to keep full access.`}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/settings?tab=billing')}
            className={`${btnBg} ${btnText} text-xs font-medium px-3 py-1 md:px-4 md:py-1.5 rounded-md flex items-center gap-1 transition-colors`}
            data-testid="trial-banner-upgrade-btn"
          >
            Upgrade
            <ArrowRight className="w-3 h-3" />
          </button>
          <button
            onClick={handleDismiss}
            className={`${text} opacity-60 hover:opacity-100 p-0.5 transition-opacity`}
            data-testid="trial-banner-dismiss-btn"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
